import React, { useMemo } from "react";

import { PathResolver, withResolvePaths } from "../../withResolvePaths";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import {
    SelectAggregationClauseWithFilter,
    useAggregationQuery,
} from "../../../Engine/Core/Filtering/UseAggregationQuery";
import { FilterExpression } from "../../../Helpers/FilterExpressions/FilterExpression";
import { useVariablesContext, VariablesContext, VariablesContextData } from "../../Services/Contexts/VariablesContext";

export interface SelectAggregationClauseWithTarget {
    select: SelectAggregationClauseWithFilter;
    targetVariableName: string;
}

export interface AggregationQueryProps {
    multilinePath: AbsoluteModelPath;
    filterUniqueKey?: string;
    resolvePath: PathResolver;
    children: React.ReactNode;
    filter?: FilterExpression;
    includeMultilineFilters: boolean;
    selects: SelectAggregationClauseWithTarget[];
    variablesPrefix: string;
}

const AggregationQuery = (props: AggregationQueryProps) => {
    const {
        multilinePath,
        resolvePath,
        filterUniqueKey = "default",
        children,
        filter,
        includeMultilineFilters,
        selects,
        variablesPrefix,
    } = props;

    const querySelects = useMemo(() => selects.map(x => x.select), [selects]);

    const data = useAggregationQuery({
        filter: filter,
        includeMultilineFilters: includeMultilineFilters,
        multiplicityPath: resolvePath(multilinePath),
        uniqueTableId: filterUniqueKey,
        selects: querySelects,
    });

    const mainFields: VariablesContextData = {
        [`${variablesPrefix}/isStale`]: data.isStale,
        [`${variablesPrefix}/isLoading`]: data.isLoading,
        [`${variablesPrefix}/error`]: data.error,
    };

    const aggregationValues: VariablesContextData = Object.fromEntries(
        selects.map((x, index) => [`${variablesPrefix}/${x.targetVariableName}`, data.result[index]])
    );

    const currentVariables = useVariablesContext();
    const nextVariables: VariablesContextData = {
        ...currentVariables,
        ...mainFields,
        ...aggregationValues,
    };

    return (
        <VariablesContext.Provider value={nextVariables}>
            <>{children}</>
        </VariablesContext.Provider>
    );
};

export const AggregationQueryContainer = withResolvePaths(AggregationQuery);
